import React, { useState, useEffect } from 'react';
import { Bookmark, Save } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import api from '../utils/api';
import { toast } from 'sonner';

const BLOCK_TYPE_LABELS = {
  'image-text-stack': 'Image + Text (Stack)',
  'image-text-row': 'Image + Text (Row)',
  columns: 'Columns',
  hero: 'Hero Banner',
  pricing: 'Pricing Table',
  testimonials: 'Testimonials',
  cta: 'Call to Action',
};

const SaveBlockTemplateModal = ({ open, onClose, block, onSaved }) => {
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');

  useEffect(() => {
    if (open && block) {
      const label = BLOCK_TYPE_LABELS[block.type] || (block.type || 'Block');
      setName(`My ${label}`);
    }
  }, [open, block]);

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Please enter a name for this block');
      return;
    }
    setSaving(true);
    try {
      // Strip the runtime block id/anchor so it gets a fresh one on insert
      const { id, ...rest } = block;
      const cleanContent = { ...(rest.content || {}) };
      delete cleanContent.anchor_id;
      const blockToSave = { ...rest, content: cleanContent };

      const res = await api.post('/templates', {
        name: name.trim(),
        category: 'block',
        kind: 'block',
        blocks: [blockToSave],
      });
      toast.success('Block saved! Find it under "My Saved Blocks" when adding a block.');
      onSaved?.(res.data);
      onClose();
    } catch (error) {
      console.error('Failed to save block:', error);
      toast.error(error.response?.data?.detail || 'Failed to save block');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="save-block-template-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bookmark size={18} className="text-indigo-600" />
            Save block as reusable
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <p className="text-sm text-slate-500">
            Save this styled block so you can quickly add it to any page without rebuilding it.
          </p>
          <div className="space-y-2">
            <Label htmlFor="block-template-name">Block name *</Label>
            <Input
              id="block-template-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Promo card"
              data-testid="save-block-name-input"
              onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); }}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="save-block-cancel">
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-block-confirm">
            <Save size={16} className="mr-2" />
            {saving ? 'Saving...' : 'Save Block'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SaveBlockTemplateModal;
